import { DashboardCard } from "@dashboard/components/Card";
import { type AtticErrorFragment } from "@dashboard/graphql";
import { Box, Checkbox, Input, Text } from "@saleor/macaw-ui-next";
import { useIntl } from "react-intl";

import {
  getAtticApiKeyErrorMessage,
  getAtticApiKeyFieldError,
  getAtticApiKeyFormErrors,
} from "../../errors";
import { formMessages } from "../../messages";
import { type AtticApiKeyGeneralFormData } from "../../types";
import { LABEL_MAX_LENGTH } from "../../utils";

export interface AtticApiKeyGeneralCardProps {
  data: AtticApiKeyGeneralFormData;
  errors: AtticErrorFragment[];
  disabled: boolean;
  /** The earliest day the picker offers: today. */
  minDay: string;
  /** An expired key: the expiry help says how to bring it back. */
  expired?: boolean;
  /** Wraps the date input, so Save can read the input's own validity. */
  expiryRef?: React.Ref<HTMLDivElement>;
  onChange: (patch: Partial<AtticApiKeyGeneralFormData>) => void;
}

/** The read-only switch's help sentence, which describes the checkbox. */
const READ_ONLY_HELP_ID = "attic-api-key-read-only-help";

/**
 * Macaw's Checkbox spreads extra props onto its <label> and forwards its ref
 * to the checkbox control itself, so aria-describedby is set through the ref.
 */
const describeBy = (id: string) => (control: HTMLButtonElement | null) =>
  control?.setAttribute("aria-describedby", id);

/**
 * Label, expiry and the read-only switch: the three plain fields of a key, on
 * create and on edit.
 */
export const AtticApiKeyGeneralCard = ({
  data,
  errors,
  disabled,
  minDay,
  expired = false,
  expiryRef,
  onChange,
}: AtticApiKeyGeneralCardProps) => {
  const intl = useIntl();
  const labelError = getAtticApiKeyFieldError(errors, "label");
  const expiryError = getAtticApiKeyFieldError(errors, "expiresAt");
  const formErrors = getAtticApiKeyFormErrors(errors, intl);

  return (
    <DashboardCard data-test-id="attic-api-key-general">
      <DashboardCard.Header>
        <DashboardCard.Title>{intl.formatMessage(formMessages.generalTitle)}</DashboardCard.Title>
      </DashboardCard.Header>
      <DashboardCard.Content>
        {formErrors.map(message => (
          <Text
            key={message}
            as="p"
            size={3}
            color="critical1"
            marginBottom={3}
            data-test-id="attic-api-key-form-error"
          >
            {message}
          </Text>
        ))}
        <Box display="grid" gridTemplateColumns={{ mobile: 1, desktop: 2 }} gap={4}>
          <Input
            label={intl.formatMessage(formMessages.label)}
            name="label"
            data-test-id="attic-api-key-label"
            disabled={disabled}
            value={data.label}
            maxLength={LABEL_MAX_LENGTH}
            onChange={event => onChange({ label: event.target.value })}
            error={!!labelError}
            helperText={
              getAtticApiKeyErrorMessage(labelError, intl) ??
              intl.formatMessage(formMessages.labelHelp, { max: LABEL_MAX_LENGTH })
            }
          />
          <div ref={expiryRef}>
            <Input
              type="date"
              label={intl.formatMessage(formMessages.expiry)}
              name="expiresAt"
              data-test-id="attic-api-key-expiry"
              disabled={disabled}
              value={data.expiryDay}
              min={minDay}
              onChange={event => onChange({ expiryDay: event.target.value })}
              error={!!expiryError}
              helperText={
                getAtticApiKeyErrorMessage(expiryError, intl) ??
                intl.formatMessage(
                  expired ? formMessages.expiryReactivates : formMessages.expiryHelp,
                )
              }
            />
          </div>
        </Box>
        <Box marginTop={5}>
          <Checkbox
            name="readOnly"
            data-test-id="attic-api-key-read-only"
            disabled={disabled}
            checked={data.readOnly}
            onCheckedChange={checked => onChange({ readOnly: checked === true })}
            ref={describeBy(READ_ONLY_HELP_ID)}
          >
            <Text size={3}>{intl.formatMessage(formMessages.readOnly)}</Text>
          </Checkbox>
          <Text
            as="p"
            id={READ_ONLY_HELP_ID}
            size={2}
            color="default2"
            marginTop={1}
            data-test-id="attic-api-key-read-only-help"
          >
            {intl.formatMessage(formMessages.readOnlyHelp)}
          </Text>
        </Box>
      </DashboardCard.Content>
    </DashboardCard>
  );
};

export default AtticApiKeyGeneralCard;
